import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Chat.module.css';
import { chatService } from '../../services/api';
import { format } from 'date-fns';

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState('new');
  
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        
        if (!token || !user) {
          navigate('/login');
          return;
        }

        await loadChatHistory();
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChatHistory = async () => {
    try {
      const response = await chatService.getHistory();
      const history = response.data.map(msg => ({
        id: msg.id,
        userMessage: msg.user_message,
        aiResponse: msg.ai_response,
        timestamp: new Date(msg.created_at)
      }));
      setChatHistory(history);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);

    const newMessage = {
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);

    try {
      const response = await chatService.sendMessage(userMessage);
      const aiMessage = {
        type: 'ai',
        content: response.data.response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        content: 'Failed to get response. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return format(new Date(timestamp), 'MMM d, h:mm a');
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentChatId('new');
  };

  const handleChatSelect = (chat) => {
    setCurrentChatId(chat.id);
    setMessages([
      {
        type: 'user',
        content: chat.userMessage,
        timestamp: new Date(chat.timestamp)
      },
      {
        type: 'ai',
        content: chat.aiResponse,
        timestamp: new Date(chat.timestamp)
      }
    ]);
  };

  return (
    <div className={styles.chatWrapper}>
      <div className={styles.chatHeader}>
        <div className={styles.headerContent}>
          <h1>
            <i className="fas fa-robot"></i>
            SmartTech AI Assistant
          </h1>
          <p>Your intelligent construction project companion</p>
        </div>
      </div>
      
      <div className={styles.chatContainer}>
        <div 
          className={`${styles.sidebarBackdrop} ${isSidebarOpen ? styles.show : ''}`}
          onClick={() => setIsSidebarOpen(false)}
        />
        <div className={`${styles.sidebar} ${isSidebarOpen ? styles.open : ''}`}>
          <div className={styles.sidebarHeader}>
            <button 
              className={styles.toggleSidebar}
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <i className="fas fa-bars"></i>
            </button>
            <button 
              className={styles.newChatButton}
              onClick={handleNewChat}
            >
              <i className="fas fa-plus"></i>
              New Conversation
            </button>
          </div>
          
          <div className={styles.historyList}>
            {currentChatId === 'new' && (
              <div className={`${styles.historyItem} ${styles.active}`}>
                <div className={styles.historyItemContent}>
                  <div className={styles.historyItemIcon}>
                    <i className="fas fa-plus-circle"></i>
                  </div>
                  <div className={styles.historyItemText}>
                    <h4>New Conversation</h4>
                    <p>Start a fresh chat</p>
                  </div>
                </div>
              </div>
            )}

            {chatHistory.map((chat) => (
              <div
                key={chat.id}
                className={`${styles.historyItem} ${currentChatId === chat.id ? styles.active : ''}`}
                onClick={() => handleChatSelect(chat)}
              >
                <div className={styles.historyItemContent}>
                  <div className={styles.historyItemIcon}>
                    <i className="fas fa-message"></i>
                  </div>
                  <div className={styles.historyItemText}>
                    <h4>
                      {chat.userMessage.length > 30 
                        ? `${chat.userMessage.substring(0, 30)}...` 
                        : chat.userMessage}
                    </h4>
                    <p>{formatTimestamp(chat.timestamp)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.chatMain}>
          <div className={styles.chatContent} ref={messagesContainerRef}>
            {messages.length === 0 ? (
              <div className={styles.welcomeScreen}>
                <div className={styles.welcomeIcon}>
                  <i className="fas fa-robot"></i>
                </div>
                <h2>How can I help you today?</h2>
                <div className={styles.suggestionGrid}>
                  <button className={styles.suggestionButton}>
                    <i className="fas fa-chart-line"></i>
                    Project Progress Updates
                  </button>
                  <button className={styles.suggestionButton}>
                    <i className="fas fa-cogs"></i>
                    Website Customization
                  </button>
                  <button className={styles.suggestionButton}>
                    <i className="fas fa-tasks"></i>
                    Implementation Status
                  </button>
                  <button className={styles.suggestionButton}>
                    <i className="fas fa-question-circle"></i>
                    General Help
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.chatMessages}>
                {messages.map((msg, index) => (
                  <div key={index} className={`${styles.message} ${styles[msg.type]}`}>
                    <div className={styles.messageContent}>
                      <div className={styles.messageAvatar}>
                        {msg.type === 'user' ? (
                          <i className="fas fa-user"></i>
                        ) : (
                          <i className="fas fa-robot"></i>
                        )}
                      </div>
                      <div className={styles.messageBody}>
                        <div className={styles.messageText}>
                          {msg.content}
                        </div>
                        <div className={styles.timestamp}>
                          {formatTimestamp(msg.timestamp)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} className={styles.scrollAnchor} />
              </div>
            )}
          </div>

          <div className={styles.chatInputWrapper}>
            <form onSubmit={handleSubmit} className={styles.chatInputForm}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message here..."
                disabled={isLoading}
                className={styles.chatInput}
              />
              <button 
                type="submit" 
                disabled={isLoading || !input.trim()}
                className={styles.sendButton}
                title={isLoading ? 'Sending...' : 'Send message'}
              >
                {isLoading ? (
                  <div className={styles.sendingIndicator}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat; 